import { useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { RootState } from "../../../../store/store";
import {
  IBillingRequestParams,
  IBillingRequestPayloadList,
} from "../../../../models/user/policyDetails/Billings";
import CustomTable, {
  IColumn,
} from "../../../../common/CustomTable/CustomTable";
import moment from "moment";
import { dateFormat } from "../../../../constants/Constants";
import { GetPolicyBillingDetails } from "../../../../store/user/policyDetails/policyDetailsActions";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ts from "typescript";
interface BillingTabComponentProps {
  agentBilling: string;
}
const BillingTabComponent: React.FC<BillingTabComponentProps> = ({
  agentBilling,
}) => {
 

  const dispatch = useAppDispatch();

  const { carrier, system, lobCode } = useParams();
  const { policyList } = useAppSelector(
    (state: RootState) => state.customerPolicies
  );
  

  const { billingDetails, policyDetailsHeader } = useAppSelector(
    (store: RootState) => store.policyDetails
  );

  const { t } = useTranslation();
  const { calledReducerType, isLoading } = useAppSelector(
    (state) => state.common
  );

  const allGeneratedInvoices = Array.isArray(billingDetails)
    ? billingDetails.flatMap((item) => item.generatedInvoices || [])
    : [];

  const allPolicyPayments = Array.isArray(billingDetails)
    ? billingDetails.flatMap((item) => item.policyPayments || [])
    : [];

  useEffect(() => {
    if (policyDetailsHeader?.accountId && carrier && lobCode && system) {
      const requestParams: IBillingRequestPayloadList = [
        {
          accountId: policyDetailsHeader?.accountId,
          carrier,
          lobCode,
          system,
        },
      ];
      dispatch(GetPolicyBillingDetails(requestParams));
    } else {
      const agentPayloads = policyList.map((policy: any) => ({
        accountId: policy.accountId,
        carrier: policy.carrier,
        lobCode: policy.lobCode,
        system: policy.system,
      }));

      const requestParams: IBillingRequestPayloadList = agentPayloads;
      dispatch(GetPolicyBillingDetails(requestParams));
    }
  }, [
    policyDetailsHeader?.accountId,
    dispatch,
    carrier,
    lobCode,
    system,
    policyList,
  ]);

  const policyPaymentColumns: IColumn[] = [
    {
      label: t("Paid Date"),
      accessorKey: "paidDate",
      Cell: (row: any) => {
        return row.paidDate ? (
          moment(row.paidDate).format(dateFormat)
        ) : (
          <Typography
            sx={{
              textAlign: "center",
              fontFamily: "Noto Sans",
              fontSize: "14px",
              fontWeight: 400,
              whiteSpace: "initial",
            }}
          >
            -
          </Typography>
        );
      },
    },
    {
      label: t("Paid Amount"),
      accessorKey: "paidAmount",
      Cell:(row:any)=>{
        return (`$${row.paidAmount}`)
      }
    },
  ];

  const generatedInvoicesColumns: IColumn[] = [
    {
      label: t("Invoice Date"),
      accessorKey: "invoiceDate",
      Cell: (row: any) => {
        return row.invoiceDate ? (
          moment(row.invoiceDate).format(dateFormat)
        ) : (
          <Typography
            sx={{
              textAlign: "center",
              fontFamily: "Noto Sans",
              fontSize: "14px",
              fontWeight: 400,
              whiteSpace: "initial",
            }}
          >
            -
          </Typography>
        );
      },
    },
    {
      label: t("Due Date"),
      accessorKey: "  dueDate",
      Cell: (row: any) => {
        return row.dueDate ? (
        moment(row.dueDate).format(dateFormat)
        ) : (
          <Typography
            sx={{
              textAlign: "center",
              fontFamily: "Noto Sans",
              fontSize: "14px",
              fontWeight: 400,
              whiteSpace: "initial",
            }}
          >
            -
          </Typography>
        );
      },
    },
    {
      label: t("Invoice Amount"),
      accessorKey: "invoiceAmount",
      Cell:(row:any)=>{
        return (`$${row.invoiceAmount}`)
      }
    },
  ];
  return (
    <Box 
      sx={{ display: "grid", gap: "30px", padding: "20px" }}
      id="billing_container"
      data-test-id="billing_container"
      aria-label="Billing information container"
    >
      {agentBilling && Array.isArray(billingDetails) ? (
        // Show tables grouped by carrierPolicyNo
        billingDetails.map((agentPolicyBilling, index) => (
          <Box
            key={agentPolicyBilling.carrierPolicyNo || index}
            sx={{
              border: "1px solid #ccc",
              borderRadius: "12px",
              padding: "20px",
              display: "grid",
              gap: "20px",
            }}
            id={`billing_policy_${index}`}
            data-test-id={`billing_policy_${index}`}
            aria-label={`Billing information for policy ${agentPolicyBilling.carrierPolicyNo}`}
          >
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: "bold",
                textAlign: "left",
              }}
              id={`billing_policy_number_${index}`}
              data-test-id={`billing_policy_number_${index}`}
              aria-label={`Policy number: ${agentPolicyBilling.carrierPolicyNo}`}
            >
              {t("Policy No")}: {agentPolicyBilling.carrierPolicyNo}
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "20px",
                "@media(max-width: 599px)": {
                  gridTemplateColumns: "1fr",
                },
              }}
              id={`billing_tables_container_${index}`}
              data-test-id={`billing_tables_container_${index}`}
              aria-label="Billing tables container"
            >
              <Box 
                sx={{ display: "grid", gap: "10px" }}
                id={`billing_invoices_section_${index}`}
                data-test-id={`billing_invoices_section_${index}`}
                aria-label="Invoices section"
              >
                <Typography
                  sx={{
                    fontFamily: "Playfair Display",
                    fontSize: "20px",
                    fontWeight: 400,
                    textAlign: "left",
                  }}
                  id={`billing_invoices_title_${index}`}
                  data-test-id={`billing_invoices_title_${index}`}
                  aria-label="Invoices title"
                >
                  {t("Invoices")}
                </Typography>
                <CustomTable
                  columns={generatedInvoicesColumns}
                  data={agentPolicyBilling.generatedInvoices || []}
                  displaySkeleton={
                    isLoading && calledReducerType === "billingDetails"
                  }
                />
              </Box>

              <Box 
                sx={{ display: "grid", gap: "10px" }}
                id={`billing_payments_section_${index}`}
                data-test-id={`billing_payments_section_${index}`}
                aria-label="Payments section"
              >
                <Typography
                  sx={{
                    fontFamily: "Playfair Display",
                    fontSize: "20px",
                    fontWeight: 400,
                    textAlign: "left",
                  }}
                  id={`billing_payments_title_${index}`}
                  data-test-id={`billing_payments_title_${index}`}
                  aria-label="Payments title"
                >
                  {t("Payments")}
                </Typography>
                <CustomTable
                  columns={policyPaymentColumns}
                  data={agentPolicyBilling.policyPayments || []}
                  displaySkeleton={
                    isLoading && calledReducerType === "billingDetails"
                  }
                />
              </Box>
            </Box>
          </Box>
        ))
      ) : (
        // Show fallback view (no grouping)
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            alignItems: "start",
            gap: "20px",
            width: "100%",
            "@media(max-width:599px)": {
              gridTemplateColumns: "1fr",
            },
          }}
          id="billing_fallback_container"
          data-test-id="billing_fallback_container"
          aria-label="Billing information container"
        >
          <Box 
            sx={{ display: "grid", gap: "20px" }}
            id="billing_invoices_section"
            data-test-id="billing_invoices_section"
            aria-label="Invoices section"
          >
            <Typography
              sx={{
                fontFamily: "Playfair Display",
                fontSize: "20px",
                fontWeight: 400,
                textAlign: "left",
              }}
              id="billing_invoices_title"
              data-test-id="billing_invoices_title"
              aria-label="Invoices title"
            >
              {t("Invoices")}
            </Typography>
            <CustomTable
              columns={generatedInvoicesColumns}
              data={allGeneratedInvoices}
              displaySkeleton={
                isLoading && calledReducerType === "billingDetails"
              }
            />
          </Box>

          <Box 
            sx={{ display: "grid", gap: "20px" }}
            id="billing_payments_section"
            data-test-id="billing_payments_section"
            aria-label="Payments section"
          >
            <Typography
              sx={{
                fontFamily: "Playfair Display",
                fontSize: "20px",
                fontWeight: 400,
                textAlign: "left",
              }}
              id="billing_payments_title"
              data-test-id="billing_payments_title"
              aria-label="Payments title"
            >
              {t("Payments")}
            </Typography>
            <CustomTable
              columns={policyPaymentColumns}
              data={allPolicyPayments}
              displaySkeleton={
                isLoading && calledReducerType === "billingDetails"
              }
            />
          </Box>
        </Box>
      )}
    </Box>
  );
 };

export default BillingTabComponent;
